import { useAuth0 } from '@auth0/auth0-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { useToast } from '../../../context/ToastContext';
import { LexOfficeBookingData, BookingImportJob, DatevBookingData } from '../../../types';
import {
  isMissingLexOfficeApiKeyError,
  createLexOfficeApiKeyErrorContent,
} from '../../../utils/lexofficeErrorHandler';
import { ProposeChangeRequest } from '../apis/types';
import {
  proposeReceiptChange,
  rejectReceipt,
  importReceipt,
} from '../apis/uploadedReceiptApi';

/**
 * Hook for receipt actions: propose change, reject, and import.
 * Replaces useReceiptStatus + useUpdateReceiptStatus.
 */
export const useReceiptActions = () => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();
  const { showToast } = useToast();

  const invalidateReceipts = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['uploaded-receipts'] });
  }, [queryClient]);

  const proposeChangeMutation = useMutation({
    mutationFn: async ({
      receiptId,
      bookingData,
    }: {
      receiptId: string;
      bookingData: ProposeChangeRequest;
    }) => {
      const token = await getAccessTokenSilently();
      return proposeReceiptChange(receiptId, bookingData, token);
    },
    onSuccess: (_data, { receiptId }) => {
      invalidateReceipts();
      queryClient.invalidateQueries({
        queryKey: ['uploaded-receipt', receiptId],
      });
    },
  });

  const rejectMutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const token = await getAccessTokenSilently();
      return rejectReceipt(receiptId, token);
    },
    onSuccess: (_data, receiptId) => {
      invalidateReceipts();
      queryClient.invalidateQueries({
        queryKey: ['uploaded-receipt', receiptId],
      });
    },
  });

  const importMutation = useMutation({
    mutationFn: async (receiptId: string) => {
      const token = await getAccessTokenSilently();
      return importReceipt(receiptId, token);
    },
    onSuccess: (_data, receiptId) => {
      invalidateReceipts();
      queryClient.invalidateQueries({
        queryKey: ['uploaded-receipt', receiptId],
      });
    },
  });

  const handleImportReceipt = useCallback(
    async (
      receiptId: string,
      hasChanges: boolean,
      proposedBookingData?: LexOfficeBookingData | DatevBookingData[]
    ): Promise<BookingImportJob | null> => {
      try {
        if (hasChanges && proposedBookingData) {
          // Propose change first, then import
          const bookingData: ProposeChangeRequest = Array.isArray(
            proposedBookingData
          )
            ? { datevBooking: proposedBookingData }
            : { lexOfficeBooking: proposedBookingData };
          await proposeChangeMutation.mutateAsync({
            receiptId,
            bookingData,
          });
        }
        const job = await importMutation.mutateAsync(receiptId);
        return job;
      } catch (error) {
        console.error('Failed to import receipt:', error);

        if (isMissingLexOfficeApiKeyError(error)) {
          showToast(
            'LexOffice API Key Required',
            createLexOfficeApiKeyErrorContent(),
            'error'
          );
        } else {
          const errorMessage =
            error instanceof Error
              ? error.message
              : error && typeof error === 'object' && 'message' in error
                ? String((error as { message: unknown }).message)
                : 'Failed to import booking data. Please try again.';
          showToast('Import Failed', errorMessage, 'error');
        }
        return null;
      }
    },
    [proposeChangeMutation, importMutation, showToast]
  );

  const handleRejectReceipt = useCallback(
    async (receiptId: string): Promise<boolean> => {
      try {
        await rejectMutation.mutateAsync(receiptId);
        return true;
      } catch (error) {
        console.error('Failed to reject receipt:', error);
        const errorMessage =
          error instanceof Error
            ? error.message
            : 'Failed to reject booking. Please try again.';
        showToast('Rejection Failed', errorMessage, 'error');
        return false;
      }
    },
    [rejectMutation, showToast]
  );

  const handleProposeChange = useCallback(
    async (
      receiptId: string,
      bookingData: LexOfficeBookingData | DatevBookingData[]
    ): Promise<boolean> => {
      try {
        const request: ProposeChangeRequest = Array.isArray(bookingData)
          ? { datevBooking: bookingData }
          : { lexOfficeBooking: bookingData };
        await proposeChangeMutation.mutateAsync({
          receiptId,
          bookingData: request,
        });
        return true;
      } catch (error) {
        console.error('Failed to propose change:', error);
        showToast(
          'Update Failed',
          'Failed to save booking data changes.',
          'error'
        );
        return false;
      }
    },
    [proposeChangeMutation, showToast]
  );

  return {
    importReceipt: handleImportReceipt,
    rejectReceipt: handleRejectReceipt,
    proposeChange: handleProposeChange,
    isImporting: importMutation.isPending,
    isRejecting: rejectMutation.isPending,
    isProposing: proposeChangeMutation.isPending,
  };
};
